/**
 * Tab Layout Class that holds and manages tabs
 * TODO: [Andrei: 02/03/16] Write a proper RegionManager with regions being Tabs
 * TODO: [Andrei: 02/03/16] Consolidate addNewEmptyTab, addTab, and addTabToLayout methods
 */
import { captureException } from "extensions/sentry";
import { View } from "extensions/marionette";
import { _ } from "extensions/underscore";
import $ from "jquery";
import Radio from "backbone.radio";

import { store } from "common/store";

import {
    EmptyTabLayoutError,
    PazienError
} from "common/helpers/errors";

import { pazienHistory } from "common/components/pazienHistory";
import { ensureErrorCallback } from "common/components/error";
import { updateStickyItemsWidth, isJqueryEvent } from "common/utils";
import { removeQueryStringParameter } from "common/helpers/removeQueryStringParameter";

import layoutTemplate from "./tabsView.jst";
import tabNavTemplate from "./tabNav.jst";
import tabContentTemplate from "./tabContent.jst";

import "./tabs.scss";

// Probably unnecessary but if just use key/name can always map to different icon later.
const iconMap = {
    cog: "fal fa-cog",
    list: "fal fa-list",
    flag: "far fa-flag",
    cards: "fal fa-perf-cards",
    warning: "fal fa-exclamation-circle"
};

export const TabsView = View.extend({
    childViewEventPrefix: "childview",
    history: true, // Indicates that this layout has to update url when active tab is changed
    propagateShowEvent: true, // Should the window.resize event be thrown when active tab changes
    
    className: "pz-tabs style-1",
    
    attributes: {
        role: "tabpanel" // Required by bootstrap tabs
    },
    
    template: layoutTemplate,
    
    ui: {
        nav: ".nav-tabs",
        addTab: ".add_tab",
        collapsed_tabs: ".collapsed_tabs",
        content: ".tab-content"
    },
    
    events: {
        "shown.bs.tab": "onTabShown",
        "click @ui.addTab": "addNewEmptyTabFromTabNavi",
        "click .closeTab": "closeTab",
        "updated:sticky:width @ui.nav": "autoCollapseNav"
    },
    
    childViewEvents: {
        "tab:close": "closeTabByChild",
        "tab:change:name": "changeTabName",
        "tab:fragment:change": "changeTabFragment"
    },
    
    initialize() {
        this.tabs = []; // Array to manage tab IDs
        this.name = this.model.get("name");
        this.hideDatePicker = this.model.get("hideDatePicker");
        this.description = this.model.get("description");
        this.section = this.model.get("section");
        
        this.listenTo(this.model, "tab:add", (model) => {
            this.addTabToLayout(model);
            this.checkIfOnlyTabLeft(model);
        });
        
        this.listenTo(this.model, "tab:remove", (model) => {
            this.removeTabFromLayout(model);
            this.checkIfOnlyTabLeft(model);
        });
        
        this.listenTo(this.model.tabs, "change:name", (model, name) => {
            // Update tab name in the DOM
            this.$(`#nav${model.id}`).find("a.tab-nav-name").html(name);
            
            // Update tab tooltip in the DOM
            if (!this.model.get("hideTooltip")) {
                this.$(`#nav${model.id}`).attr("aria-label", name);
            }
        });
        
        this.listenTo(this.model.tabs, "change:fragment", this.onChangeFragment);
        
        this.on("render", function() {
            this.canActivateTab = true;
            
            if (_.isFunction(this.activateTabWhenReady)) {
                this.activateTabWhenReady();
            }
        });
        
        this.listenTo(Radio.channel("tabEvents"), "action", (region, event, value, options) => {
            // Check if current tabsView contains a tab with requested id
            if (this.getRegion(region)) {
                const viewInstance = this.getChildView(region);
                if (viewInstance) {
                    viewInstance.triggerMethod(event, viewInstance, value, options);
                }
            }
        });
    
        this.listenTo(Radio.channel("tabEvents"), "autocollapse", this.autoCollapseNav);
    },
    
    showTabViewInRegion({ region, view }) {
        // Check if we're dealing with Promise, not actual view
        const showView = async(v) => {
            // If tab is not `active` we should defer view rendering and loading when it actually becomes as active
            try {
                this.showChildView(region, v);
            } catch (error) {
                const { TabErrorContent } = await import("./tabErrorContent");
                this.showChildView(region, new TabErrorContent({
                    message: error.message,
                    stack: error.stack
                }));
                console.error(error);
                captureException(new PazienError({
                    message: "Unable to render a tab",
                    originalError: error
                }));
            }
        };
        
        if (view.then) {
            view.then((boundView) => {
                showView(boundView);
            });
        } else {
            showView(view);
        }
    },
    
    onChangeFragment(model) {
        // Get view from controller and
        const view = model.getTabViewByFragment();
        
        // Replace current tab view
        if (view) {
            this.showTabViewInRegion({ region: model.id, view });
        }
    },
    
    checkIfOnlyTabLeft() {
        if (this.model.get("canCloseLastTab") === false) {
            this.$(`#nav${this.tabs[0]} .closeTab`).toggleClass("hidden", this.tabs.length === 1);
        } else if (this.model.tabs.length === 0) {
            this.triggerMethod("layout:empty", this);
        }
    },
    
    onTabShown(e) {
        const tabID = e.target ? $(e.target).attr("aria-controls") : e;
        const tabModel = this.model.tabs.get(tabID);
        
        // Lazy load the tab
        if (tabModel && _.isFunction(tabModel.lazyLoad)) {
            tabModel.lazyLoad();
            delete tabModel.lazyLoad;
        }
        
        window.requestAnimationFrame(async() => {
            if (this.isDestroyed()) { return; }
            
            try {
                const { Highcharts } = await import(
                    /* webpackChunkName: "highcharts_lib" */
                    "extensions/highcharts"
                );
                Highcharts.contains(this.getChildView(tabID).el).forEach((chart) => chart.reflow());
            } catch (error) {
                ensureErrorCallback(error);
            }
        });
        
        if (!this.getOption("propagateShowEvent") // If nested tab layout throws an event of changed tab, we still don't want to propagate it to the parent
            && e.stopPropagation) {
            e.stopPropagation();
        }
        
        this.triggerURLChange(e);
        
        this.model.activateTab(tabID);
        this.removeTooltipFromTab(tabID);
        
        updateStickyItemsWidth();
    
        // Check if the active tab header under collapsed section and move it right before the grouping tab
        if (this.ui.collapsed_tabs.has(`#nav${tabID}`).length) {
            this.$(`#nav${tabID}`).insertBefore(this.ui.nav.children("li[role='presentation']").last());
            this.autoCollapseNav();
        }
    
        this.triggerMethod("tab:after:shown", {
            tabModel,
            currentView: this.getChildView(tabID)
        });
    },
    
    triggerURLChange(obj, opts) {
        if (this.getOption("history")) {
            const tabID = isJqueryEvent(obj) ? $(obj.target).attr("aria-controls") : obj;
            const correspondingTab = this.model.tabs.get(tabID);
            const fragment = correspondingTab ? correspondingTab.get("fragment") : null;
            
            if (correspondingTab && fragment) {
                pazienHistory.navigateWithParams(fragment, opts);
            }
        }
    },
    
    _addNav(opts) {
        const nav = tabNavTemplate;
        const $navEl = $(nav(opts));
        if (this.model.get("collapse")) {
            $navEl.insertBefore(this.ui.collapsed_tabs);
        } else if (this.model.get("newTabButton")) {
            $navEl.insertBefore(this.ui.addTab);
        } else {
            $navEl.appendTo(this.ui.nav);
        }
        this.autoCollapseNav();
    },
    
    autoCollapseNav() {
        if (!this.model.get("collapse") || !this.ui.nav[0].clientWidth) {
            return;
        }
        
        // Collapse
        const collapsedTabsCont = this.ui.collapsed_tabs.find(".all_collapsed_tabs");
        if (this.ui.nav[0].clientWidth < this.ui.nav[0].scrollWidth - 7) {
            const allTabs = this.ui.nav.children("li:not(.add_tab):not(.collapsed_tabs):not(.active)").toArray();
            
            // We should make collapsed tabs container visible the moment we find it will be used, so it's space is included in nav clientWidth later
            this.ui.collapsed_tabs.removeClass("hidden");
            
            while (this.ui.nav[0].clientWidth < this.ui.nav[0].scrollWidth - 7 && allTabs.length) {
                collapsedTabsCont.prepend(allTabs.pop());
            }
        } else if (collapsedTabsCont.children().length) {
            while (this.ui.nav[0].clientWidth >= this.ui.nav[0].scrollWidth - 7 && collapsedTabsCont.children().length > 0) {
                collapsedTabsCont.children().first().insertBefore(this.ui.collapsed_tabs);
            }
            
            // Run the func again to remove any extra tab back that broke the layout
            this.autoCollapseNav();
        }
        
        const hasCollapsedItems = collapsedTabsCont.children().length;
        
        // Sort items in collapsed container
        if (hasCollapsedItems) {
            collapsedTabsCont.append(collapsedTabsCont.children().sort((a, b) => {
                const valA = a.textContent.trim();
                const valB = b.textContent.trim();
                return (valA > valB) || (valA === valB) - 1;
            }));
        }
    
        // show/hide collapsed tabs if there are children
        this.ui.collapsed_tabs.toggleClass("hidden", !hasCollapsedItems);
    },
    
    makeNavItemActive(id) {
        if (this.isRendered()) {
            this.ui.nav.children("li.active").removeClass("active");
            this.ui.nav.children(`#nav${id}`).addClass("active");
        }
    },
    
    getActiveTabModelID() {
        if (!this.ui.nav.children) {
            return false;
        }
        
        const activeTabNavID = this.ui.nav.children("li.active").attr("id");
        
        if (activeTabNavID) {
            return activeTabNavID.replace("nav", "");
        }
        
        return false;
    },
    
    _addContent(opts) {
        const content = tabContentTemplate;
        this.ui.content.append($(content(opts)));
    },
    
    makeContentItemActive(id) {
        try {
            this.ui.content.children(".tab-pane.active").removeClass("active");
            this.ui.content.children(`#${id}`).addClass("active");
        } catch {
            console.error("An error has occurred with `makeContentItemActive`.");
        }
    },
    
    /**
     * Add new Tab to a tabular layout
     *
     * @param {Tab} tabModel Tab model
     * @returns {Region} Newly added Marionette Region
     */
    addTabToLayout(tabModel) {
        // Check if a tab with the same id has been rendered already in the layout
        if (!_.contains(this.tabs, tabModel.id) || tabModel.get("newTab")) {
            const { id } = tabModel;
            const name = tabModel.get("name");
            
            // Add region so it can be used later by lazyLoader
            this.addRegion(id, `#${id}`);
            this.tabs.push(id);
            
            this._addNav({
                id,
                name,
                tooltip: tabModel.get("hideTooltip") ? null : tabModel.get("tooltip") || name,
                hideCloseIcon: tabModel.get("hideCloseIcon"),
                icon: tabModel.get("settingsIcon") ? iconMap.cog : iconMap[tabModel.get("icon")] // TODO - Refactor the settingsIcon property and find places called and just make icon.
            });
            
            this._addContent({
                id,
                name
            });
        }
        
        if (tabModel.get("active")) {
            this.makeTabActive(tabModel.id);
        }
    },
    
    removeTooltipFromTab(id) {
        const tooltip = this.$(`#nav${id}`).data("bs.tooltip");
        if (tooltip) {
            tooltip.destroy();
        }
    },
    
    removeTabFromLayout(model) {
        const region = this.getRegion(model.id);
        
        if (!region) {
            throw new Error("Cannot remove the tab because region is not defined");
        }
        
        // If closing currently active tab then next tab becomes active.
        // If next tab doesn't exist then previous becomes a new active tab
        if (region.$el.hasClass("active")) {
            const nextActiveTab = region.$el.next()[0] || region.$el.prev()[0];
            const nextActiveTabID = $(nextActiveTab).attr("id");
            
            if (nextActiveTabID) {
                this.makeTabActive(nextActiveTabID);
            } else if (this.model.get("canCloseLastTab") === false) {
                throw new EmptyTabLayoutError({
                    message: "Layout does not have any tabs to display",
                    extra: {
                        layout: model.get("name")
                    }
                });
            }
            
            // Take a history fragment from the active tab and replace current url
            if (this.getOption("history")) {
                const nextActiveTabFragment = this.model.tabs.get(nextActiveTabID).get("fragment");
                if (nextActiveTabFragment) {
                    pazienHistory.navigateWithParams(nextActiveTabFragment, {
                        replace: true
                    });
                }
            }
        }
        
        // Destroy the current view, if there is one. If there is no current view, it does nothing and returns immediately.
        region.empty();
        
        // Destroy the region and all views under the tab
        region.destroy();
        
        // Remove tooltip if any
        this.removeTooltipFromTab(model.id);
        
        // Remove navigation
        this.$(`#nav${model.id}`).remove();
        
        // Remove tab frame
        this.$(`#${model.id}`).remove();
        
        // Remove entry from tabs dictionary
        const indexInTabs = this.tabs.indexOf(model.id);
        if (indexInTabs > -1) {
            this.tabs.splice(indexInTabs, 1);
        }
    
        this.autoCollapseNav();
    
        return region;
    },
    
    addTab(tabModelProps, newTab) {
        const alreadyRendered = this.isTabAlreadyPresentInLayout(tabModelProps);
        if (alreadyRendered && !newTab) {
            return this.makeTabActive(alreadyRendered.id);
        } if (newTab) {
            tabModelProps.newTab = true;
        }
        
        const tabModel = this.model.addTab(tabModelProps, this.getActiveTabModelID());
        
        this.showTabViewInRegion({ region: tabModel.id, view: tabModel.getTabView() });
        
        return tabModel;
    },
    
    /**
     * Makes tab with passed ID as currently active
     * @param {String} id tab identifier to become active
     */
    makeTabActive(id) {
        // If tab cannot be activated now, then schedule activation as soon as rendering of a container is done
        if (this.canActivateTab !== true) {
            this.activateTabWhenReady = _.partial(this.makeTabActive, id);
            return;
        }
        
        // Use bootstrap tab-show method to follow full tab event lifecycle
        this.$(`#nav${id} a`).tab("show");
        
        this.makeNavItemActive(id);
        this.makeContentItemActive(id);
    },
    
    makeTabActiveByIndex() {
        if (!this.tabs.length) { return; }
        this.makeTabActive(this.tabs[0]);
    },
    
    addNewEmptyTabFromTabNavi(e) {
        e.preventDefault();
        if (store.getState().permissions.user_permissions.feature_multi_tabs) {
            this.addNewEmptyTab();
        } else {
            // Handle disabled multiple tabs package level permission
            Radio.channel("main").request("disabled:permission", "feature_multi_tabs");
        }
    },
    
    getNewTabObject(fragment = this.model.get("rootFragment")) {
        const tabClone = _.clone(this.getOption("defaultTab"));
        
        return $.extend(true, tabClone, {
            fragment
        });
    },
    
    /**
     * Adds a new empty tab in layouts where layout model has newTabButton set to true
     * It takes defaultTab value passed from controller and resets fragment to root fragment of the section
     */
    addNewEmptyTab() {
        const tabObject = this.getNewTabObject();
        
        if (this.getOption("history")) {
            pazienHistory.navigateWithParams(tabObject.fragment);
        }
    },
    
    closeTab(e) {
        const tabToClose = $(e.currentTarget).attr("data-close");
        this.model.removeTab(tabToClose);
    },
    
    /**
     * Add tab to layout, but don't render a view inside
     * @param tab
     */
    lazyRenderTab(tab) {
        // Store lazy loader in tab model. Not the best solution from the MVC perspective, but... ok... rewrite later.
        tab.lazyLoad = () => {
            // If tab is not `active` we should defer view rendering and loading when it actually becomes as active
            const view = tab.getTabView();
            if (view) {
                this.showTabViewInRegion({ region: tab.id, view });
            }
        };
        
        try {
            // Add tab title and frame to the layout
            this.addTabToLayout(tab);
        } catch (error) {
            this.model.removeTab(tab);
            captureException(error);
        }
    },
    
    /**
     * Render tabs
     */
    renderTabs() {
        //  Check if Layout model contains predefined tabs, and if it does then lazyRender those tab
        const hasStoredTabs = !!this.model.tabs.length;
        if (hasStoredTabs) {
            this.model.tabs.each(this.lazyRenderTab, this);
        }
        
        // If it doesn't have stored tabs or layout specifically states to alwaysShowDefaultTab then add default tab also
        if (!hasStoredTabs || this.model.get("alwaysShowDefaultTab")) {
            // Then add requested default tab
            this._renderDefaultTab();
        }
        
        this.checkIfOnlyTabLeft();
    },
    
    _renderDefaultTab() {
        const defaultTab = this.getOption("defaultTab");
        
        // If layout doesn't have a default tab then return earlier
        if (!defaultTab) {
            return;
        }
        
        // This code below to not add default tab to right panel in analytics layouts
        if (this.model.tabs.length && this.getOption("omitDefaultTab")) {
            return;
        }
        
        // If default tab already present in the layout then don't add it (just activate).
        // Otherwise add and activate. Clone to make sure we don't work with a direct reference.
        const tab = _.clone(defaultTab);
        const alreadyRendered = this.isTabAlreadyPresentInLayout(tab);
        
        if (alreadyRendered) {
            this.makeTabActive(alreadyRendered.id);
        } else {
            this.addTab(tab, true);
        }
    },
    
    /**
     * Check if tab is already in the layout
     * @param tab
     * @returns {Tab} returns Tab if found
     */
    isTabAlreadyPresentInLayout(tab) {
        return this.model.tabs.find((t) => t.get("fragment") === tab.fragment || t.get("id") === tab.id);
    },
    
    onRender() {
        this.renderTabs();
        this.autoCollapseNav();
        
        if (this.model.get("collapse")) {
            // Listen to window.resize to update collapsed tabs
            $(window).on(`resize.tabsCollapser.${this.cid}`, () => {
                window.requestAnimationFrame(() => {
                    if (this.isDestroyed()) { return; }
                    this.autoCollapseNav();
                });
            });
        }
    },
    
    onDestroy() {
        $(window).off(`resize.tabsCollapser.${this.cid}`);
    },
    
    _getTabModelFromTabView(view) {
        return this.model.tabs.get(view.tabID);
    },
    
    closeTabByChild(child) {
        this.model.removeTab(child.tabID);
    },
    
    changeTabName(child, cb) {
        const tab = this._getTabModelFromTabView(child);
        
        if (tab) {
            cb(tab);
        } else {
            throw new Error("Cannot find change tab name because requested tab does not exist in current tab layout");
        }
    },
    
    changeTabFragment(child, newFragment, { trigger = false, replace = false, newTab = false } = {}) {
        let tab;
        let tabId;
        
        if (newTab) {
            if (!store.getState().permissions.user_permissions.feature_multi_tabs) {
                Radio.channel("main").request("disabled:permission", "feature_multi_tabs");
                return;
            }
            
            tab = this.addTab(this.getNewTabObject(newFragment), true);
            tabId = tab.id;
        } else {
            tab = this._getTabModelFromTabView(child);
            tabId = child.tabID;
        }
        
        tab.set("fragment", removeQueryStringParameter(newFragment));
        this.triggerURLChange(tabId, { trigger, replace });
    },
    
    getActiveTabView() {
        const activeID = this.getActiveTabModelID();
        if (activeID) {
            return this.getChildView(activeID);
        }
        
        return null;
    }
});
